import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { WORK_CATEGORIES, categoryLabel, RECUBRIMIENTOS, QUALITY_OPTIONS, UNITS } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Stack, Plus, Trash, CheckCircle, Camera, PencilSimple } from "@phosphor-icons/react";

const emptyForm = {
  category: "civil", recubrimiento: "", name: "", quality: "", unit: "m²",
  quantity_installed: "", quantity_total: "", percent_complete: "", terminated: false,
  contract_id: "", contractor_id: "",
};

const qualityTone = (q) => ({
  Excelente: "bg-emerald-50 text-emerald-700", Buena: "bg-blue-50 text-blue-700",
  Regular: "bg-amber-50 text-amber-700", Deficiente: "bg-red-50 text-red-700",
}[q] || "bg-slate-100 text-slate-600");

export default function WorkItems() {
  const [items, setItems] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/workitems").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/contracts").then((r) => setContracts(r.data)); api.get("/contractors").then((r) => setContractors(r.data)); }, []);

  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const openNew = () => { setForm(emptyForm); setEditId(null); setOpen(true); };
  const openEdit = (w) => {
    setForm({ ...emptyForm, ...w, contract_id: w.contract_id || "", contractor_id: w.contractor_id || "" });
    setEditId(w.id); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      quantity_installed: parseFloat(form.quantity_installed || 0),
      quantity_total: parseFloat(form.quantity_total || 0),
      percent_complete: parseFloat(form.percent_complete || 0),
      contract_id: form.contract_id || null,
      contractor_id: form.contractor_id || null,
    };
    try {
      if (editId) {
        await api.put(`/workitems/${editId}`, {
          name: payload.name, recubrimiento: payload.recubrimiento, quality: payload.quality, unit: payload.unit,
          quantity_installed: payload.quantity_installed, quantity_total: payload.quantity_total,
          percent_complete: payload.percent_complete, terminated: payload.terminated,
        });
      } else {
        await api.post("/workitems", payload);
      }
      toast.success("Partida guardada"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addPhoto = async (id, fileObj) => {
    const fd = new FormData(); fd.append("file", fileObj);
    await api.post(`/workitems/${id}/photo`, fd); toast.success("Foto agregada"); load();
  };

  const remove = async (id) => { if (!window.confirm("¿Eliminar partida?")) return; await api.delete(`/workitems/${id}`); load(); };

  const shown = filter ? items.filter((i) => i.category === filter) : items;
  const presetVals = WORK_CATEGORIES.map((c) => c.value);
  const extraCats = [...new Set(items.map((i) => i.category).filter((v) => v && !presetVals.includes(v)))].map((v) => ({ value: v, label: v }));
  const allCats = [...WORK_CATEGORIES, ...extraCats];
  const grouped = allCats.map((c) => ({ ...c, items: shown.filter((i) => i.category === c.value) })).filter((g) => g.items.length);

  return (
    <div>
      <PageHeader overline="Avance y calidad" title="Partidas de obra"
        action={isManager && <PrimaryButton onClick={openNew} data-testid="add-workitem-btn"><Plus size={18} weight="bold" /> Nueva partida</PrimaryButton>} />

      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors duration-200 ${filter === "" ? "bg-primary text-white border-primary" : "bg-white border-input text-muted-foreground hover:bg-muted"}`}>Todas</button>
        {allCats.map((c) => (
          <button key={c.value} onClick={() => setFilter(c.value)} data-testid={`filter-${c.value}`}
            className={`px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors duration-200 ${filter === c.value ? "bg-primary text-white border-primary" : "bg-white border-input text-muted-foreground hover:bg-muted"}`}>{c.label}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={Stack} title="Sin partidas" subtitle="Registra partidas por actividad (civil, eléctrica, acabados…) con calidad, cantidad y avance."
          action={isManager && <PrimaryButton onClick={openNew}><Plus size={18} weight="bold" /> Nueva partida</PrimaryButton>} />
      ) : (
        <div className="space-y-8">
          {grouped.map((g) => (
            <div key={g.value}>
              <h2 className="font-heading font-bold text-xl mb-3">{g.label} <span className="text-muted-foreground text-sm font-sans">({g.items.length})</span></h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {g.items.map((w) => (
                  <Card key={w.id} data-testid={`workitem-${w.id}`} className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-heading font-bold text-lg leading-tight">{w.name}</div>
                        {w.recubrimiento && <div className="text-sm text-primary font-medium">{w.recubrimiento}</div>}
                        <div className="text-xs text-muted-foreground mt-0.5">{w.contractor_name}</div>
                      </div>
                      <div className="flex gap-1">
                        {isManager && <button onClick={() => openEdit(w)} className="p-1 text-muted-foreground hover:text-primary"><PencilSimple size={16} /></button>}
                        {canDelete && <button onClick={() => remove(w.id)} className="p-1 text-muted-foreground hover:text-danger"><Trash size={16} /></button>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-3">
                      {w.quality && <span className={`px-2 py-0.5 rounded-sm text-xs font-semibold ${qualityTone(w.quality)}`}>{w.quality}</span>}
                      {w.terminated
                        ? <span className="inline-flex items-center gap-1 text-success text-xs font-semibold"><CheckCircle size={13} weight="fill" /> Terminado</span>
                        : <span className="text-xs text-muted-foreground">En proceso</span>}
                    </div>

                    <div className="text-sm text-muted-foreground mt-2">
                      Instalado: <b className="text-foreground">{w.quantity_installed}</b>{w.quantity_total ? ` / ${w.quantity_total}` : ""} {w.unit}
                    </div>

                    <div className="mt-2">
                      <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Avance</span><b>{w.percent_complete}%</b></div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary transition-all duration-500" style={{ width: `${Math.min(w.percent_complete, 100)}%` }} />
                      </div>
                    </div>

                    {w.photos?.length > 0 && (
                      <div className="flex gap-2 mt-3 overflow-x-auto">
                        {w.photos.map((p, i) => <img key={i} src={fileUrl(p.path)} alt="" className="w-16 h-16 rounded-sm object-cover border border-border flex-shrink-0" />)}
                      </div>
                    )}

                    <label className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary font-medium cursor-pointer hover:underline" data-testid={`add-photo-${w.id}`}>
                      <Camera size={16} weight="bold" /> Agregar foto
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && addPhoto(w.id, e.target.files[0])} />
                    </label>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Editar partida" : "Nueva partida"}>
        <form onSubmit={save} className="space-y-4">
          {!editId && (
            <Field label="Actividad / especialidad *">
              <input list="workcat-list" data-testid="workitem-category" className={inputClass} value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ej. Terracerías" required />
              <datalist id="workcat-list">
                {WORK_CATEGORIES.map((c) => <option key={c.value} value={c.label} />)}
              </datalist>
            </Field>
          )}
          {form.category === "acabados" && (
            <Field label="Recubrimiento">
              <input data-testid="workitem-recubrimiento" list="recub-list" className={inputClass} placeholder="Ej. Pintura, piso porcelanato…"
                value={form.recubrimiento} onChange={(e) => setForm({ ...form, recubrimiento: e.target.value })} />
              <datalist id="recub-list">{RECUBRIMIENTOS.map((r) => <option key={r} value={r} />)}</datalist>
            </Field>
          )}
          <Field label="Nombre de la partida *"><input data-testid="workitem-name" required className={inputClass} placeholder="Ej. Muro fachada eje 3" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>

          {!editId && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Contratista">
                <select className={inputClass} value={form.contractor_id} onChange={(e) => setForm({ ...form, contractor_id: e.target.value })}>
                  <option value="">—</option>
                  {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Contrato (financiero)">
                <select data-testid="workitem-contract" className={inputClass} value={form.contract_id} onChange={(e) => setForm({ ...form, contract_id: e.target.value })}>
                  <option value="">—</option>
                  {contracts.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </Field>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Field label="Cant. instalada"><input type="number" step="0.01" className={inputClass} value={form.quantity_installed} onChange={(e) => setForm({ ...form, quantity_installed: e.target.value })} /></Field>
            <Field label="Cant. total"><input type="number" step="0.01" className={inputClass} value={form.quantity_total} onChange={(e) => setForm({ ...form, quantity_total: e.target.value })} /></Field>
            <Field label="Unidad">
              <select className={inputClass} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Calidad">
              <select data-testid="workitem-quality" className={inputClass} value={form.quality} onChange={(e) => setForm({ ...form, quality: e.target.value })}>
                <option value="">—</option>
                {QUALITY_OPTIONS.map((q) => <option key={q} value={q}>{q}</option>)}
              </select>
            </Field>
            <Field label="% Avance"><input data-testid="workitem-percent" type="number" step="1" className={inputClass} value={form.percent_complete} onChange={(e) => setForm({ ...form, percent_complete: e.target.value })} /></Field>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
            <input type="checkbox" data-testid="workitem-terminated" checked={form.terminated} onChange={(e) => setForm({ ...form, terminated: e.target.checked })} className="w-4 h-4 accent-primary" />
            Marcar como terminado
          </label>

          <PrimaryButton type="submit" disabled={busy} data-testid="save-workitem-btn">{busy ? "Guardando…" : "Guardar partida"}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
